<template>
  <div class="sonar-quiz">
    <div class="sonar-quiz__wrapper">
      <SonarAssessmentWelcome v-if="showWelcomeCard" />
      <section
        v-else
        class="sonar-quiz__resource-container"
        :class="[
          questionWithoutResource && 'sonar-quiz__resource-container--hidden',
          noLabQuestion && 'sonar-quiz__resource-container--half-size',
          questionImages.length > 1 &&
            'sonar-quiz__resource-container--multiple-images',
          ...contentCSSClases,
        ]"
      >
        <div
          v-if="!questionWithoutResource && !isLabStopped"
          class="sonar-quiz__maximize-icon"
          @click="toggleContent"
        >
          <ListIcon v-if="isContentExpanded" />
          <Maximize2Icon v-else />
        </div>
        <SonarLabSession
          v-if="showStartLabCard"
          class="sonar-quiz__lab-start"
        />
        <SonarLabIframe
          v-if="showLabIframe"
          class="sonar-quiz__lab-iframe"
          :class="labLayoutExpanded && 'sonar-quiz__lab-iframe--expanded'"
        />
        <div v-if="questionContentIsImage" class="sonar-quiz__content-image">
          <SonarImage
            v-for="(image, index) in questionImages"
            :key="`qi-${index}`"
            class="sonar-quiz__image"
            :imageURL="image.content_id"
          />
        </div>
      </section>
      <aside
        :class="[
          'sonar-quiz__sidebar',
          questionWithoutResource && 'sonar-quiz__sidebar--expanded',
          noLabQuestion && 'sonar-quiz__sidebar--half-size',
        ]"
      >
        <SonarAssessmentSidebar
          v-bind="{
            isLabStopped,
            isFirstLabQuestion,
            showWelcomeCard,
            isLabRunning,
            isCtaLoading,
          }"
        />
      </aside>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CONSTANTS from '@/constants'
import { Maximize2Icon, ListIcon } from 'vue-feather-icons'
import SonarLabSession from './lab/SonarLabSession.vue'
import SonarAssessmentSidebar from './quiz/SonarAssessmentSidebar.vue'
import SonarLabIframe from './lab/SonarLabIframe.vue'
import SonarAssessmentWelcome from './SonarAssessmentWelcome.vue'
import SonarImage from './SonarImage.vue'

export default {
  name: 'SonarQuiz',
  components: {
    SonarAssessmentSidebar,
    SonarLabIframe,
    SonarImage,
    Maximize2Icon,
    ListIcon,
    SonarLabSession,
    SonarAssessmentWelcome,
  },
  props: {
    assessment: {
      type: Object,
      required: true,
    },
    hasLab: {
      type: Boolean,
      required: true,
    },
    isLabRunning: {
      type: Boolean,
      required: false,
    },
    isFirstLabQuestion: {
      type: Boolean,
      required: true,
    },
    showWelcomeCard: {
      type: Boolean,
      required: true,
    },
    isLabStopped: {
      type: Boolean,
      required: true,
      default: () => false,
    },
    isCtaLoading: {
      type: Boolean,
      required: true,
      default: () => false,
    },
  },
  data() {
    return {
      contentExpanded: false,
      contentCSSClases: [],
      contentCSSPrefix: 'sonar-quiz__resource-container',
    }
  },
  watch: {
    questionWithoutResource() {
      this.contentCSSClases = []
    },
  },
  computed: {
    ...mapState({
      currentLab: state => state.labs.currentLab,
    }),
    ...mapGetters({
      isLastQuestionAnswered: 'sonar/isLastQuestionAnswered',
      currentQuestion: 'sonar/currentQuestion',
      isSwitchingLabs: 'sonar/isSwitchingLabs',
      isAssessmentStarted: 'sonar/isAssessmentStarted',
    }),
    showLabIframe() {
      return (
        this.hasLab &&
        this.currentQuestion.hasLab &&
        this.isLabRunning &&
        !this.isLastQuestionAnswered &&
        !this.isSwitchingLabs
      )
    },
    questionImages() {
      return this.questionContentIsImage && this.currentQuestion.modality
    },
    questionContentIsImage() {
      return this.currentQuestion.modality.every(
        m => m.content_type === CONSTANTS.CONTENT_TYPES.IMAGE
      )
    },
    questionWithoutResource() {
      return !this.currentQuestion?.modality?.length && this.isAssessmentStarted
    },
    noLabQuestion() {
      return !this.questionWithoutResource && !this.currentQuestion.hasLab
    },
    isContentExpanded() {
      return this.contentExpanded && !this.questionWithoutResource
    },
    labLayoutExpanded() {
      return (
        this.hasLab && this.currentQuestion.hasLab && this.isContentExpanded
      )
    },
    showStartLabCard() {
      return (
        (this.currentQuestion.hasLab &&
          this.hasLab &&
          !this.isLabRunning &&
          !this.isLastQuestionAnswered) ||
        (this.hasLab && this.isSwitchingLabs && !this.isLastQuestionAnswered)
      )
    },
  },
  methods: {
    toggleContent() {
      this.contentExpanded = !this.contentExpanded
      if (this.questionWithoutResource) return
      const modifier = this.contentExpanded ? '--expanded' : '--minimized'
      return (this.contentCSSClases = `${this.contentCSSPrefix}${modifier}`)
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-quiz {
  width: 100%;
  height: 100%;
  min-height: 280px;
  &__wrapper {
    height: 100%;
    position: relative;
    display: flex;
  }
  &__resource-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 100%;
    height: 100%;
    padding: 20px 40px;
    background: var(--glass-mid-gray), var(--black);
    flex: 1 0 calc(100% - 385px);
    overflow-y: auto;
    &--hidden {
      width: 0;
      padding: 0;
      flex: 0;
      transition: flex 0 linear;
    }
    &--expanded {
      transition: flex 150ms linear !important;
      flex: 1 0 calc(100%) !important;
    }
    &--minimized {
      transition: flex 150ms linear;
    }
    &--half-size {
      flex: 1 0 50%;
    }
    &--multiple-images {
      align-items: flex-start;
    }
  }
  &__content-image {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  &__maximize-icon {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 41px;
    height: 41px;
    background: var(--glass-mid-gray) var(--dark-brighter);
    color: var(--white);
    padding: 10px;
    border-radius: 30px;
    right: 0;
    top: 0;
    z-index: 1000;
    cursor: pointer;
    svg {
      width: 15px;
    }
  }
  &__iframe-wrapper {
    position: relative;
    overflow: hidden;
    width: 100%;
    padding-top: 56.25%;
  }
  &__iframe {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border-radius: 10px;
  }
  &__lab-iframe {
    width: 100%;
    height: 100%;
  }
  &__lab-start {
    height: 570px;
  }
  &__sidebar {
    flex: 0 0 385px;
    width: 385px;
    background: var(--glass-dark-gray), var(--black);
    box-shadow: 0px 10px 14px rgba(0, 0, 0, 0.4);
    height: 100%;
    &--expanded {
      width: 100%;
      flex: 0 0 100%;
    }
    &--half-size {
      flex: 1 0 50%;
    }
  }
}
</style>
